/**
 * PRD 管理器
 * 管理 PRD 的生命周期和状态
 */

import fs from 'fs-extra';
import path from 'path';
import matter from 'gray-matter';
import { logger } from '../utils/logger';
import { TaskStore } from './task-store';
import { ExtendedTask } from '../types/extended-task';

export interface PRDMetadata {
  id: string;
  title: string;
  version: string;
  author: string;
  date: string;
  status: 'draft' | 'review' | 'approved' | 'in_progress' | 'completed' | 'archived';
  progress?: number; // 0-100
  taskIds?: string[];
  completedTasks?: number;
  totalTasks?: number;
}

export interface PRD {
  metadata: PRDMetadata;
  content: string;
  path: string;
}

export class PRDManager {
  private prdDir: string;
  private taskStore: TaskStore;
  
  constructor(workflowDir?: string) {
    const projectRoot = workflowDir || process.cwd();
    this.prdDir = path.join(projectRoot, '.workflow', 'docs', 'prds');
    this.taskStore = new TaskStore(projectRoot);
  }
  
  /**
   * 获取所有 PRD
   */
  async listPRDs(): Promise<PRD[]> {
    if (!await fs.pathExists(this.prdDir)) {
      return [];
    }
    
    const files = await fs.readdir(this.prdDir);
    const prdFiles = files.filter(f => f.endsWith('.md'));
    
    const prds: PRD[] = [];
    for (const file of prdFiles) {
      const prd = await this.getPRD(file.replace('.md', ''));
      if (prd) {
        prds.push(prd);
      }
    }
    
    return prds;
  }
  
  /**
   * 获取指定 PRD
   */
  async getPRD(prdId: string): Promise<PRD | null> {
    const filepath = path.join(this.prdDir, `${prdId}.md`);
    
    if (!await fs.pathExists(filepath)) {
      return null;
    }
    
    const fileContent = await fs.readFile(filepath, 'utf-8');
    const { data: metadata, content } = matter(fileContent);
    
    return {
      metadata: metadata as PRDMetadata,
      content,
      path: filepath,
    };
  }
  
  /**
   * 更新 PRD 元数据
   */
  async updatePRDMetadata(prdId: string, updates: Partial<PRDMetadata>): Promise<PRD | null> {
    const prd = await this.getPRD(prdId);
    if (!prd) {
      logger.error(`PRD not found: ${prdId}`);
      return null;
    }
    
    // 合并元数据
    const newMetadata = {
      ...prd.metadata,
      ...updates,
    };
    
    // 重新生成文件内容
    const newContent = matter.stringify(prd.content, newMetadata);
    
    // 保存文件
    await fs.writeFile(prd.path, newContent, 'utf-8');
    logger.info(`Updated PRD metadata: ${prdId}`);
    
    return {
      metadata: newMetadata,
      content: prd.content,
      path: prd.path,
    };
  }
  
  /**
   * 更新 PRD 进度
   */
  async updatePRDProgress(prdId: string): Promise<void> {
    // 获取与 PRD 关联的所有任务
    const tasks = await this.taskStore.getTasksByPrdId(prdId);
    
    if (tasks.length === 0) {
      logger.warn(`No tasks found for PRD: ${prdId}`);
      return;
    }
    
    // 计算完成的任务数
    const completedTasks = tasks.filter(t => 
      t.status === 'done' || t.status === 'archived'
    ).length;
    
    // 计算进度百分比
    const progress = Math.round((completedTasks / tasks.length) * 100);
    
    // 确定 PRD 状态
    let status: PRDMetadata['status'] = 'in_progress';
    if (progress === 0) {
      status = 'approved'; // 假设任务创建后 PRD 已被批准
    } else if (progress === 100) {
      status = 'completed';
    }
    
    // 更新 PRD 元数据
    await this.updatePRDMetadata(prdId, {
      progress,
      completedTasks,
      totalTasks: tasks.length,
      taskIds: tasks.map(t => t.id),
      status,
    });
    
    logger.info(`Updated PRD progress: ${prdId} (${progress}%)`);
  }
  
  /**
   * 监听任务状态变更
   */
  async onTaskStatusChange(task: ExtendedTask): Promise<void> {
    if (!task.prdId) {
      return;
    }
    
    // 异步更新 PRD 进度，不阻塞任务操作
    this.updatePRDProgress(task.prdId).catch(error => {
      logger.error('Failed to update PRD progress:', error);
    });
  }
  
  /**
   * 获取 PRD 统计信息
   */
  async getPRDStats(prdId: string): Promise<{
    total: number;
    completed: number;
    inProgress: number;
    blocked: number;
    byType: Record<string, number>;
    byPriority: Record<string, number>;
  } | null> {
    const tasks = await this.taskStore.getTasksByPrdId(prdId);
    
    if (tasks.length === 0) {
      return null;
    }
    
    const stats = {
      total: tasks.length,
      completed: 0,
      inProgress: 0,
      blocked: 0,
      byType: {} as Record<string, number>,
      byPriority: {} as Record<string, number>,
    };
    
    for (const task of tasks) {
      // 状态统计
      if (task.status === 'done' || task.status === 'archived') {
        stats.completed++;
      } else if (task.status === 'in_progress') {
        stats.inProgress++;
      } else if (task.status === 'blocked') {
        stats.blocked++;
      }
      
      // 类型统计
      const type = task.type || 'other';
      stats.byType[type] = (stats.byType[type] || 0) + 1;
      
      // 优先级统计
      stats.byPriority[task.priority] = (stats.byPriority[task.priority] || 0) + 1;
    }
    
    return stats;
  }
  
  /**
   * 创建 PRD 报告
   */
  async generatePRDReport(prdId: string): Promise<string> {
    const prd = await this.getPRD(prdId);
    if (!prd) {
      throw new Error(`PRD not found: ${prdId}`);
    }
    
    const stats = await this.getPRDStats(prdId);
    if (!stats) {
      return '## No tasks found for this PRD';
    }
    
    const report = `# PRD Progress Report

## Overview
- **PRD ID**: ${prd.metadata.id}
- **Title**: ${prd.metadata.title}
- **Status**: ${prd.metadata.status}
- **Progress**: ${prd.metadata.progress || 0}%
- **Last Updated**: ${new Date().toISOString()}

## Task Statistics
- **Total Tasks**: ${stats.total}
- **Completed**: ${stats.completed} (${Math.round(stats.completed / stats.total * 100)}%)
- **In Progress**: ${stats.inProgress}
- **Blocked**: ${stats.blocked}

## Tasks by Type
${Object.entries(stats.byType)
  .map(([type, count]) => `- **${type}**: ${count}`)
  .join('\n')}

## Tasks by Priority
${Object.entries(stats.byPriority)
  .map(([priority, count]) => `- **${priority}**: ${count}`)
  .join('\n')}

---
Generated on ${new Date().toLocaleString()}
`;

    return report;
  }
}